import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, API, fileUrl, imgUrl } from "@/lib/api";
import { Trash2, X, Upload, Image as ImageIcon } from "lucide-react";

// -------- Site Images (admin) --------
export function SiteImagesAdmin() {
  const [slots, setSlots] = useState([]);
  const load = () => api.get("/site-images").then(r => setSlots(r.data.slots || []));
  useEffect(() => { load(); }, []);
  const upload = async (slot, file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      await api.put(`/admin/site-images/${slot}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${slot} updated`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    }
  };
  const remove = async (slot) => {
    if (!confirm(`Remove custom image for '${slot}'? The site will fall back to the default.`)) return;
    await api.delete(`/admin/site-images/${slot}`);
    toast.success("Removed");
    load();
  };
  const [showCard, setShowCard] = useState(false);
  const setFocal = async (slot, focal_x, focal_y) => {
    setSlots(prev => prev.map(s => s.slot === slot ? { ...s, focal_x, focal_y } : s));
    try {
      await api.patch(`/admin/site-images/${slot}/focal`, { focal_x, focal_y });
    } catch { toast.error("Could not save focal point"); load(); }
  };
  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="font-serif text-4xl mb-1">Site Images</h1>
          <div className="overline text-white/40">Backgrounds & feature photos across the storefront</div>
        </div>
        <button onClick={() => setShowCard(true)} data-testid="preview-share-card-btn"
          className="inline-flex items-center gap-2 px-4 py-2 border border-white/20 text-xs uppercase tracking-widest hover:border-white/50 hover:bg-white/5">
          <ImageIcon size={13}/> Preview share card
        </button>
      </div>
      {showCard && <ShareCardPreview onClose={() => setShowCard(false)} />}
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6" data-testid="site-images-grid">
        {slots.map(s => {
          const hasFocal = s.focal_x != null && s.focal_y != null;
          return (
          <div key={s.slot} className="surface p-5" data-testid={`site-image-slot-${s.slot}`}>
            <div className="overline text-white/40 mb-1">{s.label}</div>
            <div className="text-[10px] text-white/30 mb-3">{s.slot} · aspect {s.aspect}</div>
            <div
              className={`aspect-video bg-black/40 mb-2 overflow-hidden flex items-center justify-center border border-white/10 relative ${s.image_url ? "cursor-crosshair" : ""}`}
              data-testid={`site-image-focal-area-${s.slot}`}
              onClick={s.image_url ? (e) => {
                const r = e.currentTarget.getBoundingClientRect();
                const x = Math.round(((e.clientX - r.left) / r.width) * 100);
                const y = Math.round(((e.clientY - r.top) / r.height) * 100);
                setFocal(s.slot, x, y);
              } : undefined}
            >
              {s.image_url
                ? <img src={imgUrl(fileUrl(s.image_url), 640)} alt={s.label} loading="lazy" decoding="async" className="w-full h-full object-cover"
                    style={hasFocal ? { objectPosition: `${s.focal_x}% ${s.focal_y}%` } : undefined} />
                : <span className="text-white/30 text-xs">Using default</span>}
              {s.image_url && hasFocal && (
                <div className="absolute w-6 h-6 -ml-3 -mt-3 rounded-full border-2 border-[#C77746] bg-[#C77746]/30 pointer-events-none"
                  style={{ left: `${s.focal_x}%`, top: `${s.focal_y}%` }} data-testid={`site-image-focal-dot-${s.slot}`} />
              )}
            </div>
            {s.image_url && (
              <div className="flex items-center justify-between mb-3 text-[10px] text-white/40">
                <span>{hasFocal ? `Focus ${s.focal_x}%, ${s.focal_y}% · fills space` : "Click image to set focus · currently fits whole image"}</span>
                {hasFocal && (
                  <button onClick={() => setFocal(s.slot, null, null)} data-testid={`site-image-focal-clear-${s.slot}`}
                    className="uppercase tracking-widest hover:text-white">Fit whole</button>
                )}
              </div>
            )}
            <div className="flex gap-2">
              <label className="flex-1 cursor-pointer inline-flex items-center justify-center gap-2 px-3 py-2 border border-white/15 text-xs uppercase tracking-widest hover:border-white/40">
                <Upload size={12}/> {s.image_url ? "Replace" : "Upload"}
                <input type="file" accept="image/*" data-testid={`site-image-upload-${s.slot}`}
                  onChange={e => upload(s.slot, e.target.files?.[0])} className="hidden" />
              </label>
              {s.image_url && (
                <button onClick={() => remove(s.slot)} data-testid={`site-image-remove-${s.slot}`}
                  className="px-3 py-2 border border-white/15 text-xs text-white/70 hover:border-red-400 hover:text-red-400">
                  <Trash2 size={12} />
                </button>
              )}
            </div>
          </div>
          );
        })}
      </div>
    </div>
  );
}

// -------- Artist Photos (admin) --------
export function ArtistPhotosAdmin() {
  const [photos, setPhotos] = useState([]);
  const load = () => api.get("/artist-gallery").then(r => setPhotos(r.data.photos || []));
  useEffect(() => { load(); }, []);
  const upload = async (file, caption) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file); if (caption) fd.append("caption", caption);
    try {
      await api.post("/admin/artist-gallery", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Photo added");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    }
  };
  const removePhoto = async (id) => {
    if (!confirm("Delete this photo?")) return;
    await api.delete(`/admin/artist-gallery/${id}`);
    toast.success("Deleted");
    load();
  };
  const updateCaption = async (id, caption) => {
    await api.patch(`/admin/artist-gallery/${id}`, { caption });
    toast.success("Caption saved");
    load();
  };
  const [batchFile, setBatchFile] = useState(null);
  const [caption, setCaption] = useState("");
  return (
    <div>
      <h1 className="font-serif text-4xl mb-1">Artist Photos</h1>
      <div className="overline text-white/40 mb-6">Photos shown in the "About the Artist" section on the home page</div>
      <div className="surface p-5 mb-6">
        <div className="overline text-white/40 mb-3">Add a new photo</div>
        <div className="flex flex-wrap items-end gap-3">
          <input type="file" accept="image/*" onChange={e => setBatchFile(e.target.files?.[0] || null)} data-testid="artist-photo-file"
            className="text-xs text-white/70"/>
          <input value={caption} onChange={e => setCaption(e.target.value)} placeholder="Caption (optional)" data-testid="artist-photo-caption"
            className="bg-transparent border border-white/15 px-3 py-2 text-sm flex-1 min-w-[240px]"/>
          <button disabled={!batchFile} onClick={() => { upload(batchFile, caption); setBatchFile(null); setCaption(""); }}
            data-testid="artist-photo-upload-btn"
            className="px-4 py-2 text-xs uppercase tracking-widest bg-white text-black disabled:opacity-40">
            Upload
          </button>
        </div>
      </div>
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6" data-testid="artist-photos-grid">
        {photos.length === 0 && <div className="text-white/40 col-span-full">No photos yet.</div>}
        {photos.map(p => (
          <div key={p.id} className="surface p-4" data-testid={`artist-photo-${p.id}`}>
            <div className="aspect-[4/5] bg-black/40 mb-3 overflow-hidden border border-white/10">
              <img src={imgUrl(fileUrl(p.image_url), 320)} alt={p.caption || ""} loading="lazy" decoding="async" className="w-full h-full object-cover" />
            </div>
            <input defaultValue={p.caption || ""} onBlur={e => e.target.value !== (p.caption || "") && updateCaption(p.id, e.target.value)}
              placeholder="Caption" data-testid={`artist-photo-caption-${p.id}`}
              className="w-full bg-transparent border border-white/10 px-2 py-1 text-xs mb-2"/>
            <button onClick={() => removePhoto(p.id)} data-testid={`artist-photo-remove-${p.id}`}
              className="text-[10px] text-white/50 hover:text-red-400 inline-flex items-center gap-1"><Trash2 size={12}/> Remove</button>
          </div>
        ))}
      </div>
    </div>
  );
}

// -------- Share card preview (Open Graph image) --------
export function ShareCardPreview({ onClose }) {
  const [stamp, setStamp] = useState(Date.now());
  const src = `${API}/og-image?t=${stamp}`;
  const copy = async () => {
    try { await navigator.clipboard.writeText("https://www.fineartbyjason.com"); toast.success("Site link copied"); }
    catch { toast.error("Could not copy"); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={onClose} data-testid="share-card-modal">
      <div className="surface max-w-3xl w-full p-6" onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <div className="font-serif text-2xl">Share card preview</div>
            <div className="text-xs text-white/50 mt-1">This is what appears when your site link is shared in texts, Facebook, Instagram, X and WhatsApp. It updates automatically from your hero image, focal point and tagline.</div>
          </div>
          <button onClick={onClose} data-testid="share-card-close" className="text-white/60 hover:text-white"><X size={18}/></button>
        </div>
        <div className="grid md:grid-cols-[1fr_auto] gap-6 items-start">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-white/40 mb-2">Facebook · X · SMS (1.91:1)</div>
            <div className="border border-white/10 overflow-hidden bg-black">
              <img src={src} alt="Share card" className="w-full block" data-testid="share-card-img" />
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-white/40 mb-2">Instagram · WhatsApp (square crop)</div>
            <div className="w-40 h-40 overflow-hidden border border-white/10 bg-black">
              <img src={src} alt="Square crop" className="w-full h-full object-cover" data-testid="share-card-square" />
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 mt-5 text-xs uppercase tracking-widest">
          <button onClick={() => setStamp(Date.now())} data-testid="share-card-refresh" className="px-3 py-2 border border-white/15 hover:border-white/40">Refresh</button>
          <a href={src} download="fine-art-by-jason-share-card.jpg" data-testid="share-card-download" className="px-3 py-2 border border-white/15 hover:border-white/40">Download</a>
          <button onClick={copy} data-testid="share-card-copy-link" className="px-3 py-2 border border-white/15 hover:border-white/40">Copy site link</button>
        </div>
        <p className="text-[11px] text-white/40 mt-4">Tip: platforms cache previews. After changing the hero or tagline, use Facebook's Sharing Debugger ("Scrape Again") to refresh it there.</p>
      </div>
    </div>
  );
}

